/**
 * CollapsiblePaneGroup — wraps a set of <CollapsiblePane>s with a
 * master "Expand all / Collapse all" toggle at the top.
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { Button, Space, theme } from 'antd';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { setAllPanesForPage } from './useCollapsibleState';

/**
 * @param {Object} props
 * @param {string} props.pageId — must match the child panes' pageId.
 * @param {React.ReactNode} props.children — <CollapsiblePane id={...} ... />
 * @param {boolean} [props.showToggle=true] — hide the master toggle if desired.
 */
export default function CollapsiblePaneGroup({ pageId, children, showToggle = true }) {
    if (!pageId) throw new Error('CollapsiblePaneGroup: pageId is required');
    const { token } = theme.useToken();
    const containerRef = useRef(null);

    const paneIds = useMemo(() => {
        // Walk children React tree — collect the CollapsiblePane ids at declaration time.
        const ids = [];
        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && child.props && child.props.id) {
                ids.push(child.props.id);
            }
        });
        return ids;
    }, [children]);

    const forceRefresh = useCallback(() => {
        // The panes read from localStorage on mount + on pageId change.
        // To flip them all at runtime we broadcast a StorageEvent AND
        // synthesise a click that re-runs their useEffect via a key change on the wrapper.
        // Simpler: reload only the pane subtree by toggling a data attribute.
        // The child panes will re-read next time they render; we force that by nudging state via localStorage keys.
        // The useCollapsibleState hook listens to (pageId, paneId) — we already updated localStorage;
        // the panes need to re-mount OR react to storage. A location.reload() is heavy; instead ask user to interact.
        // For simplicity, force a soft re-render by focusing away and back:
        if (containerRef.current) {
            containerRef.current.setAttribute('data-refresh', String(Date.now()));
        }
    }, []);

    const expandAll = useCallback(() => {
        setAllPanesForPage(pageId, true, paneIds);
        forceRefresh();
        // Kick a state change on each pane by dispatching a custom event.
        window.dispatchEvent(new CustomEvent(`policylens:collapsible:${pageId}`, { detail: { expanded: true } }));
    }, [pageId, paneIds, forceRefresh]);

    const collapseAll = useCallback(() => {
        setAllPanesForPage(pageId, false, paneIds);
        forceRefresh();
        window.dispatchEvent(new CustomEvent(`policylens:collapsible:${pageId}`, { detail: { expanded: false } }));
    }, [pageId, paneIds, forceRefresh]);

    return (
        <div ref={containerRef} data-collapsible-group={pageId}>
            {showToggle && (
                <Space style={{ marginBottom: 8 }}>
                    <Button
                        size="small"
                        icon={<ChevronDown size={14} />}
                        onClick={expandAll}
                        style={{ color: token.colorTextSecondary }}
                    >
                        Expand all
                    </Button>
                    <Button
                        size="small"
                        icon={<ChevronRight size={14} />}
                        onClick={collapseAll}
                        style={{ color: token.colorTextSecondary }}
                    >
                        Collapse all
                    </Button>
                </Space>
            )}
            {children}
        </div>
    );
}
